'use client';

import utilsStyles from '@/styles/modules/utils.module.scss';
import { type ReactNode, createContext, useContext, useState } from 'react';
import type { PostOptionFormParams } from '../../../features/lottery/api/v2/postOptionForm';
import OptionCollectionList from '../../../features/lottery/components/OptionCollectionList/OptionCollectionList';
import OptionSelectedList from '../../../features/lottery/components/OptionSelectedList/OptionSelectedList';
import type { LotteryOptionGoodsForm } from '../../../features/lottery/types';
import OptionBar from '../OptionBar/OptionBar';
import { TradeProvider } from '../TradePageClient/TradePageClient';

export const OptionProvider = TradeProvider;

type OptionStepContextType = {
  step: 'quantity' | 'name';
  setStep: (step: 'quantity' | 'name') => void;
};

const OptionStepContext = createContext<OptionStepContextType | undefined>(undefined);

export function useOptionStep() {
  const context = useContext(OptionStepContext);
  return context;
}

export function OptionPageClient({
  optionFormData,
  goodsId,
  cutoffId,
  isEditMode = false,
  lotteryHistoryId,
  orderId,
  postOptionForm,
  skipProvider = false,
  selectedCountComponent,
  headingComponent,
  isPastDeadline = false,
  optionDefaultSetType,
}: {
  optionFormData: LotteryOptionGoodsForm;
  goodsId: string;
  cutoffId: string;
  isEditMode?: boolean;
  lotteryHistoryId?: number;
  orderId?: string;
  postOptionForm: (params: PostOptionFormParams) => Promise<void>;
  skipProvider?: boolean;
  selectedCountComponent?: ReactNode;
  headingComponent?: ReactNode;
  isPastDeadline?: boolean;
  optionDefaultSetType: LotteryOptionGoodsForm['optionDefaultSetType'];
}) {
  const handlePostOptionForm = async (params: PostOptionFormParams) => {
    await postOptionForm(params);
  };

  const [namedSelectableStep, setNamedSelectableStep] = useState<'quantity' | 'name'>('quantity');

  const isOptionSelectedList = () => {
    // オプションフォームデータがない場合は表示しない
    if (!optionFormData) return false;
    // ステップがない場合は表示しない
    if (namedSelectableStep === undefined) return false;
    // ステップが'name'の場合は表示しない
    if (namedSelectableStep === 'name') return false;
    // 締められている場合は表示しない
    if (isPastDeadline) return false;
    return true;
  };

  const content = (
    <OptionStepContext.Provider value={{ step: namedSelectableStep, setStep: setNamedSelectableStep }}>
      {headingComponent}
      {selectedCountComponent}
      <div className={`${utilsStyles.mtXSmall}`}>
        <OptionCollectionList
          optionFormData={[optionFormData]}
          isEditMode={isEditMode}
          step={namedSelectableStep}
          onStepChange={setNamedSelectableStep}
          optionDefaultSetType={optionDefaultSetType}
        />
      </div>
      {isOptionSelectedList() && <OptionSelectedList optionFormData={[optionFormData]} />}
      <OptionBar
        optionFormData={optionFormData}
        goodsId={goodsId}
        cutoffId={cutoffId}
        isEditMode={isEditMode}
        lotteryHistoryId={lotteryHistoryId}
        orderId={orderId}
        postOptionForm={handlePostOptionForm}
        step={namedSelectableStep}
        onStepChange={setNamedSelectableStep}
      />
    </OptionStepContext.Provider>
  );

  if (skipProvider) {
    return content;
  }

  return <OptionProvider>{content}</OptionProvider>;
}
